import React, { useEffect, useState } from 'react'

import { Eye, EyeSlash } from '@gravity-ui/icons'
import { Button, Input, InputGroup, Label, toast } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { authApi } from '@/api/auth.api'
import authBg from '@/assets/auth-bg.png'
import { Card, CardContent } from '@/components/ui/Card'
import { useAuthStore } from '@/store/auth.store'

export default function AuthPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const { login, isLoading, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/'
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, location])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await login({ email, password })
    } catch (err) {
      // Error is handled by store
    }
  }

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    setIsResetting(true)
    try {
      await authApi.forgotPassword({ email })
      toast.success(
        t('auth.toast.forgot_password_success')
      )
      setIsForgotPassword(false)
    } catch (err) {
      toast.danger((err as Error).message || t('common.error'))
    } finally {
      setIsResetting(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center p-5 relative"
      style={{ backgroundImage: `url(${authBg})` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-0 transition" />

      <div className="w-full max-w-md relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="text-center mb-10">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-blue-600 items-center justify-center text-white text-2xl font-bold mb-4 shadow-xl shadow-blue-500/20">
            W
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight mb-2">
            {t('auth.company_name')}
          </h1>
          <p className="text-white/50 font-medium">
            {t('auth.system_name')}
          </p>
        </div>

        <Card className="bg-white/5 border-white/10 shadow-2xl">
          <CardContent className="p-8">
            {!isForgotPassword ? (
              <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs font-bold text-white/40 uppercase tracking-wider ml-1">
                    {t('auth.email')}
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    variant={'secondary'}
                    required
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between items-center ml-1 mr-1">
                    <Label className="text-xs font-bold text-white/40 uppercase tracking-wider">
                      {t('auth.password')}
                    </Label>
                    <button
                      type="button"
                      onClick={() => setIsForgotPassword(true)}
                      className="text-xs text-blue-500 hover:text-blue-400 font-semibold focus:outline-none transition-colors cursor-pointer"
                    >
                      {t('auth.forgot_password_link')}
                    </button>
                  </div>
                  <InputGroup>
                    <InputGroup.Input
                      type={isVisible ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                    <InputGroup.Suffix className="pr-0">
                      <Button
                        isIconOnly
                        aria-label={isVisible ? "Hide password" : "Show password"}
                        size="sm"
                        variant="ghost"
                        onPress={() => setIsVisible(!isVisible)}
                      >
                        {isVisible ? <Eye className="size-4" /> : <EyeSlash className="size-4" />}
                      </Button>
                    </InputGroup.Suffix>
                  </InputGroup>
                </div>

                <Button
                  type="submit"
                  fullWidth
                  isDisabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('auth.signing_in')}
                    </div>
                  ) : t('auth.sign_in')}
                </Button>
              </form>
            ) : (
              <form className="flex flex-col gap-6" onSubmit={handleForgotPasswordSubmit}>
                <div className="text-center mb-2">
                  <h2 className="text-xl font-bold text-white mb-2">
                    {t('auth.forgot_password_title')}
                  </h2>
                  <p className="text-white/60 text-sm">
                    {t('auth.forgot_password_desc')}
                  </p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs font-bold text-white/40 uppercase tracking-wider ml-1">
                    {t('auth.email')}
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    variant={'secondary'}
                    required
                  />
                </div>

                <div className="flex flex-col gap-2 mt-2">
                  <Button
                    type="submit"
                    fullWidth
                    isDisabled={isResetting}
                  >
                    {isResetting ? (
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('auth.forgot_password_submitting')}
                      </div>
                    ) : t('auth.forgot_password_submit')}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    fullWidth
                    onClick={() => setIsForgotPassword(false)}
                    className="text-white/75 hover:text-white"
                  >
                    {t('auth.back_to_login')}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="mt-10 text-center">
          <p className="text-white/40 text-xs font-bold uppercase tracking-widest">
            &copy; 2026 {t('auth.company_name')}
          </p>
        </div>
      </div>
    </div>
  )
}
